import { Component, Input, OnChanges, SimpleChanges, Inject, ViewChild } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatPaginator } from '@angular/material/paginator';
import { MatTableDataSource } from '@angular/material/table';
import { SelectionModel } from '@angular/cdk/collections';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { catchError } from 'rxjs/operators';
import { of } from 'rxjs';

import { ClassesEntity } from '../../../../../../models/entities/classes-entity';
import { IClassesEntityService } from '../../../../../../interfaces/entities/classes-entity-service.interface';
import { CLASSES_ENTITY_SERVICE_TOKEN } from '../../../../../../injection-tokens/entity-service-tokens';
import { Log } from '../../../../../../services/logger.service';
import { AdhocParticipantsDialogComponent } from './adhoc-participants-dialog.component';
import { environment } from '../../../../../../../environments/environment';
import { DEFAULT_PAGINATOR_PAGE_SIZE, PAGINATOR_PAGE_SIZE_OPTIONS } from '../../../../../../shared/pagination-options';

// ─── Helpers ────────────────────────────────────────────────────────────────
const lastPrefixKey = (classId: string) => `adhoc_last_prefix_${classId}`;

export interface ParticipantRow {
  loginId: string;
  isAdhoc: boolean;
  seqNo?: number;    // present for ad-hoc users; used for stable sort in export
  password?: string; // present for ad-hoc users; loaded from API, for export only
}

@Component({
  selector: 'app-participants-settings',
  templateUrl: './participants-settings.component.html',
  styleUrls: ['./participants-settings.component.css'],
})
export class ParticipantsSettingsComponent implements OnChanges {
  @Input() classEntity!: ClassesEntity;
  @Input() classId!: string;

  /**
   * Maximum number of participants allowed, derived upstream in SettingsTabComponent from
   * the intersection of gross VM slot capacity and ACC subscription capacity (each minus a
   * class-template reservation). Usage-independent — it is a planning ceiling, not a count
   * of what can be provisioned right now. null = no constraint in effect (class has no
   * resource types, or VM quota data is still loading).
   */
  @Input() maxParticipants: number | null = null;

  /**
   * True while VM subscription quota data is still being fetched from Azure.
   * When true the participant tab shows a 'calculating capacity…' notice instead
   * of a hard-blocked state, so the operator knows to wait for quota data.
   */
  @Input() isCapacityLoading = false;

  /**
   * VM-derived participant cap passed from SettingsTabComponent.
   * Gross VM slots across all subscriptions − 1 (permanent class-template reservation).
   * Usage-independent — does not shrink as participant VMs are provisioned.
   * null = class has no VM resource type (or quota is still loading).
   */
  @Input() vmWise: number | null = null;

  /**
   * REMAINING provisionable VMs right now (core limit − current usage), passed from
   * SettingsTabComponent. Informational only — shown as a "provisionable now" stat in the
   * capacity drawer, never used as the cap. null = loading or no VM resource type.
   */
  @Input() remainingVmCapacity: number | null = null;

  /** True when any contributing VM subscription's quota cache is stale. */
  @Input() isQuotaStale = false;

  /**
   * ACC-derived participant cap passed from SettingsTabComponent.
   * null = class has no ACC resource type.
   */
  @Input() accWise: number | null = null;

  /**
   * Whether the class-template VM has been provisioned.
   * @deprecated No longer affects the capacity calculation — the class-template −1 reservation
   * is now permanent (the gross slot count is usage-independent). Retained only so the parent
   * binding does not break; safe to remove once the binding is dropped.
   */
  @Input() isVmTemplateReady = false;

  @ViewChild(MatPaginator) set paginator(pag: MatPaginator) {
    if (pag) this.tableSource.paginator = pag;
  }

  participants: ParticipantRow[] = [];
  tableSource = new MatTableDataSource<ParticipantRow>([]);
  displayedColumns = ['select', 'loginId', 'type', 'actions'];
  selection = new SelectionModel<ParticipantRow>(true, []);
  readonly pageSizeOptions = PAGINATOR_PAGE_SIZE_OPTIONS;
  readonly pageSize = DEFAULT_PAGINATOR_PAGE_SIZE;

  bulkAddCtrl = new FormControl('');
  accessViaEmail = false;
  accessViaEmailLoading = false;
  isSaving = false;
  isLoadingAdhoc = false;
  /** Controls the capacity-breakdown info drawer. */
  isCapacityInfoOpen = false;

  // Map of lowercased adhocLoginId → {password, seqNo}
  private adhocCache = new Map<string, { password: string; seqNo: number }>();

  private readonly logger = Log.create(ParticipantsSettingsComponent);

  constructor(
    @Inject(CLASSES_ENTITY_SERVICE_TOKEN) private readonly classService: IClassesEntityService,
    private readonly snackBar: MatSnackBar,
    private readonly dialog: MatDialog,
  ) {}

  // ─── Derived state ───────────────────────────────────────────────────────

  get devTools(): boolean {
    return environment.features.devTools;
  }

  get hasAdhocCredentials(): boolean {
    return this.participants.some(p => p.isAdhoc);
  }

  get adhocCount(): number {
    return this.participants.filter(p => p.isAdhoc).length;
  }

  get lastUsedPrefix(): string {
    return localStorage.getItem(lastPrefixKey(this.classId)) ?? '';
  }

  // ─── Capacity-gate helpers ───────────────────────────────────────────────

  /**
   * How many more participants can be enrolled, or null when the limit is unknown.
   * Takes the current live count (including optimistically-added rows) into account.
   */
  get remainingSlots(): number | null {
    if (this.maxParticipants === null) return null;
    return Math.max(0, this.maxParticipants - this.participants.length);
  }

  /** True when the enforced cap has been reached or exceeded. */
  get isAtCapacity(): boolean {
    return this.maxParticipants !== null && this.participants.length >= this.maxParticipants;
  }

  /**
   * One-line summary shown in the capacity gutter.
   * Returns empty string when no participant cap applies.
   */
  get capacityLabel(): string {
    if (this.maxParticipants === null) return '';
    const used = this.participants.length;
    const max  = this.maxParticipants;
    const rem  = Math.max(0, max - used);
    return rem === 0
      ? `Participant capacity reached — ${max} maximum for this class.`
      : `${rem} of ${max} participant slot${max !== 1 ? 's' : ''} remaining.`;
  }

  // ─── Capacity gutter helpers ─────────────────────────────────────────────

  /**
   * Filled fraction (0–100) for the capacity gutter bar.
   * 0 when no cap is known.
   */
  get capacityBarPercent(): number {
    if (this.maxParticipants === null || this.maxParticipants === 0) return 0;
    return Math.min(100, Math.round((this.participants.length / this.maxParticipants) * 100));
  }

  /**
   * Gutter bar fill colour based on filled fraction.
   * Bands: <50% green · 50–74% yellow · 75–89% orange · ≥90% red.
   */
  get capacityBarColor(): string {
    const pct = this.capacityBarPercent;
    if (pct >= 90) return '#e53935';
    if (pct >= 75) return '#fb8c00';
    if (pct >= 50) return '#fbc02d';
    return '#43a047';
  }

  /**
   * Which constraint is binding.
   * 'vm' | 'acc' | 'both' | null (when neither applies or loading).
   */
  get constraintSource(): 'vm' | 'acc' | 'both' | null {
    const v = this.vmWise;
    const a = this.accWise;
    if (v === null && a === null) return null;
    if (v === null) return 'acc';
    if (a === null) return 'vm';
    if (v === a)    return 'both';
    return v < a ? 'vm' : 'acc';
  }

  /** True when the informational "provisionable right now" stat can be shown in the drawer. */
  get hasRemainingStat(): boolean {
    return this.remainingVmCapacity !== null;
  }

  // ─── Lifecycle ───────────────────────────────────────────────────────────

  ngOnChanges(_: SimpleChanges): void {
    if (!this.classId) return;

    const ts = this.classEntity?.trainingSchedule as any;
    this.accessViaEmail =
      ts?.accessViaInvitedEmailOnly ?? ts?.AccessViaInvitedEmailOnly ?? false;

    this.loadParticipants();
  }

  private loadParticipants(): void {
    const raw: any[] = (this.classEntity?.trainingSchedule as any)?.participants ?? [];

    const initial: ParticipantRow[] = raw
      .map(p =>
        typeof p === 'string'
          ? p
          : (p.email ?? p.Email ?? p.loginId ?? p.LoginId ?? ''),
      )
      .map(s => (typeof s === 'string' ? s.trim() : String(s).trim()))
      .filter(Boolean)
      .map(id => ({ loginId: id, isAdhoc: false }));

    this.isLoadingAdhoc = true;
    this.classService
      .getAdhocParticipantListByClassId(this.classId)
      .pipe(catchError(() => of([])))
      .subscribe((adhocRows: any[]) => {
        this.isLoadingAdhoc = false;
        // Merge API results into the cache without clearing it first.
        // Clearing would wipe passwords that the ad-hoc dialog just supplied
        // when ngOnChanges fires before the API round-trip finishes.
        // The API overwrites any matching key; dialog-supplied entries are preserved
        // for keys the API doesn't return (e.g. users created milliseconds ago).
        for (const r of (adhocRows ?? [])) {
          const id = (r.loginId ?? r.LoginId ?? '').trim();
          if (id) {
            this.adhocCache.set(id.toLowerCase(), {
              password: r.password ?? r.Password ?? '',
              seqNo: r.seqNo ?? r.SeqNo ?? 0,
            });
          }
        }
        // Merge: include any participants added locally (e.g. via the adhoc dialog) that
        // are not yet reflected in classEntity.trainingSchedule.participants. This prevents
        // a race condition where the async API response overwrites rows that afterClosed()
        // already added to this.participants while the request was in-flight.
        const initialIds = new Set(initial.map(r => r.loginId.toLowerCase()));
        const localExtra = this.participants.filter(p => !initialIds.has(p.loginId.toLowerCase()));
        const base = [...initial, ...localExtra];

        this.participants = base.map(row => {
          const cached = this.adhocCache.get(row.loginId.toLowerCase());
          return cached
            ? { ...row, isAdhoc: true, password: cached.password, seqNo: cached.seqNo }
            : row;
        });
        this.refreshTable();
      });
  }

  private refreshTable(): void {
    const naturalCompare = (a: string, b: string): number =>
      a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' });

    const sorted = [...this.participants].sort((a, b) => {
      if (a.isAdhoc !== b.isAdhoc) return a.isAdhoc ? 1 : -1;
      return naturalCompare(a.loginId, b.loginId);
    });
    this.participants = sorted;
    this.tableSource.data = sorted;
    this.selection.clear();
  }

  // ─── Checkbox helpers ────────────────────────────────────────────────────

  isAllSelected(): boolean {
    return this.selection.selected.length === this.tableSource.data.length;
  }

  masterToggle(): void {
    if (this.isAllSelected()) {
      this.selection.clear();
    } else {
      this.tableSource.data.forEach(row => this.selection.select(row));
    }
  }

  removeSelected(): void {
    const removedIds = new Set(this.selection.selected.map(r => r.loginId));
    this.participants = this.participants.filter(p => !removedIds.has(p.loginId));
    this.refreshTable();
    this.persist();
  }

  removeAll(): void {
    if (!this.participants.length) return;
    this.participants = [];
    this.selection.clear();
    this.refreshTable();
    this.persist();
  }

  removeAdHoc(): void {
    if (!this.participants.some(p => p.isAdhoc)) return;
    this.participants = this.participants.filter(p => !p.isAdhoc);
    this.selection.clear();
    this.refreshTable();
    this.persist();
  }

  // ─── Add participants (bulk) ─────────────────────────────────────────────

  /**
   * Handles Enter key in the bulk-add textarea.
   * Enter alone: prevents the default newline and submits the bulk add.
   * Shift+Enter: allows the default newline so the user can type on a new line.
   */
  onBulkEnter(event: Event): void {
    const ke = event as KeyboardEvent;
    if (ke.shiftKey) return; // let Shift+Enter insert a newline normally
    ke.preventDefault();
    this.addBulk();
  }

  addBulk(): void {
    const raw = this.bulkAddCtrl.value ?? '';
    const incoming = raw
      .split(/[\n\r\t,;]+/)
      .map((s: string) => s.trim().toLowerCase())
      .filter((s: string) => s.length > 0 && s.includes('@'));

    const existing = new Set(this.participants.map(p => p.loginId.toLowerCase()));
    const novel = [...new Set(incoming)].filter((e: string) => !existing.has(e));

    if (!novel.length) {
      this.snackBar.open('No new valid email addresses found.', 'Dismiss', { duration: 2500 });
      return;
    }

    // Capacity gate: reject the entire batch when it would exceed the known cap.
    // This is a UI-layer guard; the API will enforce it independently once hardened.
    const remaining = this.remainingSlots;
    if (remaining !== null && novel.length > remaining) {
      this.snackBar.open(
        remaining === 0
          ? `Participant capacity reached — cannot add more (${this.maxParticipants} max for this class).`
          : `Cannot add ${novel.length} email${novel.length !== 1 ? 's' : ''} — only ${remaining} slot${remaining !== 1 ? 's' : ''} remaining (${this.maxParticipants} max total).`,
        'Dismiss',
        { duration: 5000 },
      );
      return;
    }

    this.participants = [
      ...this.participants,
      ...novel.map((e: string) => ({ loginId: e, isAdhoc: false })),
    ];
    this.refreshTable();
    this.bulkAddCtrl.reset();
    this.persist();
  }

  removeParticipant(loginId: string): void {
    this.participants = this.participants.filter(p => p.loginId !== loginId);
    this.refreshTable();
    this.persist();
  }

  // ─── Access via invited email only toggle ────────────────────────────────

  toggleAccessViaEmail(value: boolean): void {
    this.accessViaEmailLoading = true;
    this.classService.updateTrainingSchedule(this.classId, {
      AccessViaInvitedEmailOnly: value,
    }).subscribe({
      next: () => {
        this.accessViaEmailLoading = false;
        this.accessViaEmail = value;
        this.snackBar.open(
          value
            ? 'Only invited emails may access this class.'
            : 'Access open — redemption link required.',
          'Dismiss',
          { duration: 3000 },
        );
      },
      error: (err: any) => {
        this.accessViaEmailLoading = false;
        this.logger.e('Toggle access via email failed:', err);
        this.snackBar.open('Failed to update access setting.', 'Dismiss', { duration: 3000 });
      },
    });
  }

  // ─── Ad-hoc dialog ──────────────────────────────────────────────────────

  openAdhocDialog(): void {
    // Capacity guard: don't open the dialog when no slots are left.
    if (this.isAtCapacity) return;

    this.dialog
      .open(AdhocParticipantsDialogComponent, {
        width: '600px',
        panelClass: 'adhoc-dialog-panel',
        data: {
          classId: this.classId,
          className: this.classEntity?.name ?? '',
          lastPrefix: this.lastUsedPrefix,
          currentParticipantIds: this.participants.map(p => p.loginId),
          // Pass remaining slots so the dialog can cap the count input.
          maxCount: this.remainingSlots ?? undefined,
        },
      })
      .afterClosed()
      .subscribe(
        (result: { added: { loginId: string; password: string; seqNo: number }[]; prefix: string } | null) => {
          if (!result?.added?.length) return;

          localStorage.setItem(lastPrefixKey(this.classId), result.prefix);

          const addedById = new Map(
            result.added.map(r => [r.loginId.toLowerCase(), r] as const),
          );
          const updated = this.participants.map((p) => {
            const added = addedById.get(p.loginId.toLowerCase());
            return added
              ? { ...p, isAdhoc: true, password: added.password, seqNo: added.seqNo }
              : p;
          });

          const existing = new Set(updated.map(p => p.loginId.toLowerCase()));
          const novel: ParticipantRow[] = result.added
            .filter(r => !existing.has(r.loginId.toLowerCase()))
            .map(r => ({ loginId: r.loginId, isAdhoc: true, password: r.password, seqNo: r.seqNo }));

          for (const r of result.added) {
            this.adhocCache.set(r.loginId.toLowerCase(), { password: r.password, seqNo: r.seqNo });
          }

          this.participants = [...updated, ...novel];
          this.refreshTable();
          // Persist already handled by the dialog; only update local state here.

          this.snackBar.open(`${result.added.length} ad-hoc participant(s) added.`, 'Dismiss', {
            duration: 3000,
            panelClass: 'snack-success',
          });
        },
      );
  }

  // ─── Unified export ──────────────────────────────────────────────────────

  exportCsv(): void {
    const emailRows = this.participants
      .filter(p => !p.isAdhoc)
      .sort((a, b) => this.naturalCompare(a.loginId, b.loginId));

    const adhocRows = this.participants
      .filter(p => p.isAdhoc)
      .sort((a, b) => this.naturalCompare(a.loginId, b.loginId));

    if (!emailRows.length && !adhocRows.length) {
      this.snackBar.open('No participants to export.', 'Dismiss', { duration: 2000 });
      return;
    }

    const className = this.classEntity?.name ?? this.classId;
    const csvRows: string[][] = [['Login ID', 'Password', 'Type']];
    for (const r of emailRows) {
      csvRows.push([r.loginId, '', 'Email']);
    }
    for (const r of adhocRows) {
      csvRows.push([r.loginId, r.password ?? '', 'Ad-hoc']);
    }

    const escape = (v: string) => `"${v.replace(/"/g, '""')}"`;
    const csv = csvRows.map(row => row.map(escape).join(',')).join('\r\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `Participants_${className.replace(/\s+/g, '_')}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  exportJson(): void {
    const adhocRows = this.participants
      .filter(p => p.isAdhoc)
      .sort((a, b) => this.naturalCompare(a.loginId, b.loginId));

    if (!adhocRows.length) {
      this.snackBar.open('No ad-hoc participants to export.', 'Dismiss', { duration: 2000 });
      return;
    }

    const className = this.classEntity?.name ?? this.classId;
    const payload = {
      classId: this.classId,
      credentials: adhocRows.map(r => ({ loginId: r.loginId, password: r.password ?? '' })),
    };
    const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `ClassCredentials_${className.replace(/\s+/g, '_')}_${this.classId}.json`;
    a.click();
    URL.revokeObjectURL(url);
  }

  exportAll(): void {
    const emailRows = this.participants
      .filter(p => !p.isAdhoc)
      .sort((a, b) => this.naturalCompare(a.loginId, b.loginId));

    const adhocRows = this.participants
      .filter(p => p.isAdhoc)
      .sort((a, b) => this.naturalCompare(a.loginId, b.loginId));

    if (!emailRows.length && !adhocRows.length) {
      this.snackBar.open('No participants to export.', 'Dismiss', { duration: 2000 });
      return;
    }

    const className = this.classEntity?.name ?? this.classId;
    const doc = new jsPDF();
    doc.setFontSize(13);
    doc.text(`Participants — ${className}`, 14, 16);

    let startY = 26;

    if (emailRows.length) {
      doc.setFontSize(9);
      doc.setTextColor(80, 80, 80);
      doc.text(`Email-based participants (${emailRows.length}) — credentials sent via email`, 14, startY);
      startY += 3;
      autoTable(doc, {
        startY,
        head: [['Login ID']],
        body: emailRows.map(r => [r.loginId]),
        styles: { fontSize: 9 },
        headStyles: { fillColor: [70, 130, 180] },
      });
      startY = (doc as any).lastAutoTable.finalY + 12;
    }

    if (adhocRows.length) {
      doc.setFontSize(9);
      doc.setTextColor(80, 80, 80);
      doc.text(`Ad-hoc participants (${adhocRows.length}) — keep confidential`, 14, startY);
      doc.text(`Hint: Double or triple click to copy "Login ID" or "Password"`, 14, startY += 6);
      autoTable(doc, {
        startY: startY += 3,
        head: [['Login ID', 'Password']],
        body: adhocRows.map(r => [r.loginId, r.password ?? '']),
        styles: { fontSize: 9 },
        headStyles: { fillColor: [46, 125, 50] },
      });
    }

    doc.save(`Participants_${className.replace(/\s+/g, '_')}.pdf`);
  }

  // ─── Utilities ──────────────────────────────────────────────────────────

  /**
   * Natural sort comparator: sorts alphabetically by string prefix, then numerically
   * within the same prefix. Handles any number of unknown prefixes, e.g.:
   *   company_a_user_1, company_a_user_2, ..., company_b_user_1, company_b_user_2, ...
   * Uses the browser-native { numeric: true } collation, consistent with refreshTable().
   */
  private naturalCompare(a: string, b: string): number {
    return a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' });
  }

  // ─── Persist to backend ──────────────────────────────────────────────────

  private persist(): void {
    this.isSaving = true;
    // Store as plain string array — matches TrainingSchedule.Participants: List<string>
    this.classService.updateClassPartial(this.classId, {
      'TrainingSchedule.Participants': this.participants.map(p => p.loginId),
    }).subscribe({
      next: () => {
        this.isSaving = false;
        this.snackBar.open('Participants updated', 'Dismiss', { duration: 2000, panelClass: 'snack-success' });
      },
      error: (err: any) => {
        this.isSaving = false;
        this.logger.e('Participant save error:', err);
        this.snackBar.open('Failed to update participants', 'Dismiss', { duration: 3000, panelClass: 'snack-error' });
      },
    });
  }
}
